/**
 * Supabase resilient helpers — monolab wacrm shared DB
 *
 * O stub `transactions` tem só FK user_id; FKs para categories/clients/
 * suppliers/cost_centers ainda não existem. Qualquer select com embedded
 * resource (ex: `*, categories(...), clients:client_id(...)`) pode falhar com
 * 400 PGRST200 "Could not find a relationship". Enquanto o agente Postgres
 * cria os FKs, o frontend precisa ser resiliente:
 *  - tenta embedded primeiro
 *  - se error.code === 'PGRST200' (ou mensagem PGRST200), refaz como `select('*')`
 *    e faz join manual no cliente buscando as tabelas relacionadas separadamente
 *    e mesclando nos objetos.
 *
 * Além disso o stub usa coluna `paid` enquanto o front usa `is_paid`.
 * Toda query que filtra por `is_paid` deve fazer fallback para `paid` quando
 * o erro indicar coluna inexistente (42703 / PGRST116 / "column ... does not exist").
 */

import { supabase } from '@/integrations/supabase/client';

export function isPGRST200Error(error: any): boolean {
  if (!error) return false;
  if (error.code === 'PGRST200') return true;
  const msg: string = String(error.message ?? error.details ?? '');
  return msg.includes('PGRST200') || msg.includes('Could not find a relationship');
}

export function isMissingColumnError(error: any, columnHint?: string): boolean {
  if (!error) return false;
  // Postgres undefined_column = 42703; PostgREST sometimes wraps as PGRST116
  if (error.code === '42703' || error.code === 'PGRST116') return true;
  const msg: string = String(error.message ?? error.details ?? '').toLowerCase();
  if (msg.includes('column') && msg.includes('does not exist')) return true;
  if (msg.includes('unknown column')) return true;
  if (columnHint && msg.includes(columnHint.toLowerCase())) {
    // se já é PGRST200, não é missing-column; o caller chama isPGRST200 antes
    if (msg.includes('does not exist') || msg.includes('unknown')) return true;
  }
  return false;
}

export function isPaidColumnMissingError(error: any): boolean {
  return isMissingColumnError(error, 'is_paid') || isMissingColumnError(error, 'paid');
}

// ---------------------------------------------------------------------------
// Normalização is_paid <-> paid
// ---------------------------------------------------------------------------

/**
 * Garante que a linha tenha `is_paid` booleano, independente de o DB expor
 * a coluna como `is_paid` (app legacy) ou `paid` (stub monolab).
 */
export function normalizePaidField<T extends Record<string, any>>(row: T): T {
  if (!row || typeof row !== 'object') return row;
  const anyRow: any = row;
  // stub tem `paid`, front espera `is_paid`
  if (anyRow.is_paid === undefined && anyRow.paid !== undefined) {
    anyRow.is_paid = !!anyRow.paid;
  }
  // se por algum motivo o DB já tem is_paid e o código legado ler `paid`
  if (anyRow.paid === undefined && anyRow.is_paid !== undefined) {
    anyRow.paid = anyRow.is_paid;
  }
  return row;
}

export function normalizePaidFields<T extends Record<string, any>>(rows: T[]): T[] {
  return rows.map((r) => normalizePaidField({ ...r }));
}

/**
 * Executa uma query que filtra por is_paid com fallback automático para coluna `paid`.
 * `build` recebe o nome da coluna a usar e deve retornar { data, error } do supabase.
 * Tenta com 'is_paid' primeiro; se coluna não existe, tenta com 'paid'.
 */
export async function executeWithPaidFallback<T>(build: (col: 'is_paid' | 'paid') => Promise<{ data: T; error: any }>): Promise<{ data: T; error: any }> {
  let result = await build('is_paid');
  if (result.error && isMissingColumnError(result.error, 'is_paid')) {
    // eslint-disable-next-line no-console
    console.warn('[supabaseResilient] is_paid missing, retry with paid', result.error?.message);
    result = await build('paid');
  }
  return result;
}

// ---------------------------------------------------------------------------
// Enriquecimento manual (fallback sem embedded)
// ---------------------------------------------------------------------------

type LookupMap = Map<string | number, any>;

/**
 * Busca categorias por ids e retorna Map id -> { id, name, ... }.
 * Se ids vazio retorna Map vazio (evita query desnecessária).
 */
export async function fetchCategoriesMap(ids: (number | string)[], userId: string): Promise<LookupMap> {
  const uniq = [...new Set(ids.filter((v) => v != null))];
  if (uniq.length === 0) return new Map();
  const numericIds = uniq.map(Number).filter((n) => Number.isFinite(n));
  if (numericIds.length === 0) return new Map();
  const { data, error } = await supabase.from('categories').select('id, name, color, type').in('id', numericIds).eq('user_id', userId);
  if (error) {
    // eslint-disable-next-line no-console
    console.warn('[supabaseResilient] fetchCategoriesMap failed', error);
    return new Map();
  }
  return new Map((data ?? []).map((r: any) => [r.id, r]));
}

export async function fetchClientsMap(ids: string[], userId: string): Promise<LookupMap> {
  const uniq = [...new Set(ids.filter(Boolean))];
  if (uniq.length === 0) return new Map();
  const { data, error } = await supabase.from('clients').select('id, name, email, phone, document_number').in('id', uniq).eq('user_id', userId);
  if (error) {
    console.warn('[supabaseResilient] fetchClientsMap failed', error);
    return new Map();
  }
  return new Map((data ?? []).map((r: any) => [r.id, r]));
}

export async function fetchSuppliersMap(ids: string[], userId: string): Promise<LookupMap> {
  const uniq = [...new Set(ids.filter(Boolean))];
  if (uniq.length === 0) return new Map();
  const { data, error } = await supabase.from('suppliers').select('id, name, email, phone, document_number').in('id', uniq).eq('user_id', userId);
  if (error) {
    console.warn('[supabaseResilient] fetchSuppliersMap failed', error);
    return new Map();
  }
  return new Map((data ?? []).map((r: any) => [r.id, r]));
}

export async function fetchCostCentersMap(ids: (number | string)[], userId: string): Promise<LookupMap> {
  const uniq = [...new Set(ids.filter((v) => v != null))];
  if (uniq.length === 0) return new Map();
  const { data, error } = await supabase.from('cost_centers').select('id, name, code, budgeted_cost').in('id', uniq).eq('user_id', userId);
  if (error) {
    console.warn('[supabaseResilient] fetchCostCentersMap failed', error);
    return new Map();
  }
  return new Map((data ?? []).map((r: any) => [r.id, r]));
}

/**
 * Enriquecimento genérico: mergea lookups nas transactions planas.
 * - categories -> row.categories / row.category
 * - clients    -> row.clients
 * - suppliers  -> row.suppliers
 * - cost_centers -> row.cost_centers
 *
 * Os callers que precisarem de outros aliases (ex: category:categories)
 * podem fazer merge pós-chamada; esta função cobre os casos mais comuns.
 */
export function enrichTransactionsWithLookups(
  rows: any[],
  opts: {
    categoriesMap?: LookupMap;
    clientsMap?: LookupMap;
    suppliersMap?: LookupMap;
    costCentersMap?: LookupMap;
  }
): any[] {
  return rows.map((row) => {
    const out: any = { ...row };
    if (opts.categoriesMap && row.category_id != null) {
      const cat = opts.categoriesMap.get(Number(row.category_id)) ?? opts.categoriesMap.get(row.category_id);
      if (cat) {
        out.categories = cat;
        (out as any).category = cat; // alias usado em useAccrualData
      }
    }
    if (opts.clientsMap && row.client_id) {
      const cli = opts.clientsMap.get(row.client_id);
      if (cli) out.clients = cli;
    }
    if (opts.suppliersMap && row.supplier_id) {
      const sup = opts.suppliersMap.get(row.supplier_id);
      if (sup) out.suppliers = sup;
    }
    if (opts.costCentersMap && row.cost_center_id != null) {
      const cc = opts.costCentersMap.get(Number(row.cost_center_id)) ?? opts.costCentersMap.get(row.cost_center_id);
      if (cc) out.cost_centers = cc;
    }
    return out;
  });
}
